import { useQuery } from '@apollo/client'
import { FetchQueryResultsDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import { getColumnSearchProps } from '@salescore/frontend-common'
import { Table } from 'antd'
import { Order, String } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { z } from 'zod'

const resultSchema = z.object({
  query: z.object({
    model: z.object({
      model: z.object({
        explore: z.string(),
      }),
    }),
  }),
  result: z.object({
    rows: z.array(z.array(z.number())),
  }),
})

type Result = z.infer<typeof resultSchema>

export const SourceStatsContent = ({ eltModelNames }: { eltModelNames: string[] }) => {
  const { data, loading } = useQuery(FetchQueryResultsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      queries: eltModelNames.map((eltModelName) => ({
        model: {
          model: {
            explore: eltModelName,
          },
        },
      })),
    },
  })

  const dataSource = data?.queryResults
    .map((x) => resultSchema.safeParse(x))
    .map((x) => (x.success ? x.data : undefined))
    .filter(isNotNullish)

  return (
    <Table
      loading={loading}
      dataSource={dataSource}
      rowKey={(record: Result) => record.query.model.model.explore}
      columns={[
        {
          title: t(`オブジェクト名`),
          ...getColumnSearchProps((x) => x.query.model.model.explore),
          sorter: Order.mapInput(
            Order.make<string>((a, b) => String.localeCompare(b)(a)),
            (x: Result) => x.query.model.model.explore,
          ),
          render: (_text, record) => record.query.model.model.explore,
        },
        {
          title: t(`取得したレコード数`),
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          ...getColumnSearchProps((x) => x.result.rows.join(', ') ?? '-'),
          // 表示は rows を連結した文字列だが数値なので、辞書順を避けて先頭セルの数値で比較する。
          // schema 上は複数行・複数列を許容するため、2 セル目以降は比較に入らない
          sorter: Order.mapInput(Order.Number, (x: Result) => x.result.rows.flat()[0] ?? 0),
          defaultSortOrder: 'descend',
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          render: (_text, record) => record.result.rows.join(', ') ?? '-',
        },
      ]}
    />
  )
}
